"use client";

import { Button, Tooltip } from "@softmax/design-system";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";

const styles = stylex.create({
  region: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "start",
    columnGap: spacing.space4,
  },
  inline: { display: "inline-grid" },
  locked: { opacity: 0.5 },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.background,
    color: colors.muted,
    padding: spacing.space4,
    outline: {
      default: "none",
      ":focus-visible": `2px solid ${colors.foreground}`,
    },
  },
  hidden: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clipPath: "inset(50%)",
    whiteSpace: "nowrap",
    borderWidth: 0,
  },
});

export function LockedRegionView({
  editing,
  label = "Live data, updated automatically",
  children,
  as: Tag = "div",
}: Readonly<{
  editing: boolean;
  label?: string | undefined;
  children: ReactNode;
  as?: "div" | "span" | undefined;
}>) {
  if (!editing) return <>{children}</>;

  return (
    <Tag {...stylex.props(styles.region, Tag === "span" && styles.inline)}>
      <span {...stylex.props(styles.hidden)}>Locked: {label}. </span>
      <Tag {...stylex.props(styles.locked)}>{children}</Tag>
      <span {...stylex.props(styles.badge)}>
        <Tooltip content={label}>
          <Button size="sm" variant="quiet" aria-label={`Locked: ${label}`}>
            <svg
              viewBox="0 0 16 16"
              width="12"
              height="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              aria-hidden="true"
            >
              <rect x="3" y="7" width="10" height="7" rx="1.2" />
              <path d="M5 7V5a3 3 0 0 1 6 0v2" />
            </svg>
          </Button>
        </Tooltip>
      </span>
    </Tag>
  );
}
